/**
 * Cloche de notifications avec dropdown desktop + plein écran mobile
 */

import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '@/services/notification.service';

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <svg className="w-6 h-6 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  );
}

function EmptyState({ size }: { size: 'sm' | 'lg' }) {
  return (
    <div className={`flex flex-col items-center justify-center px-4 ${size === 'lg' ? 'py-20' : 'py-12'}`}>
      <svg className={`${size === 'lg' ? 'w-16 h-16 mb-4' : 'w-12 h-12 mb-3'} text-gray-300`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      <p className={`text-gray-500 text-center ${size === 'sm' ? 'text-sm' : ''}`}>Aucune nouvelle notification</p>
    </div>
  );
}

function NotificationList({ notifications, isLoading, onItemClick, onMarkAsRead, variant }: {
  notifications: Notification[];
  isLoading: boolean;
  onItemClick: (n: Notification) => void;
  onMarkAsRead?: (id: string) => void;
  variant: 'mobile' | 'desktop';
}) {
  if (isLoading) return <LoadingSpinner />;
  if (notifications.length === 0) return <EmptyState size={variant === 'mobile' ? 'lg' : 'sm'} />;
  return (
    <div className="divide-y divide-gray-100">
      {notifications.map((n) => (
        <NotificationItem key={n.id} notification={n} onClick={onItemClick} onMarkAsRead={onMarkAsRead} variant={variant} />
      ))}
    </div>
  );
}

export function NotificationBell() {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const {
    isOpen, setIsOpen,
    notifications, isLoading, unreadCount,
    markAsReadMutation, markAllAsReadMutation,
    handleNotificationClick, handleMarkAllAsRead,
  } = useNotifications();

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-11 h-11 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
        aria-label="Notifications"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: Full screen via Portal */}
          {createPortal(
            <div className="md:hidden fixed inset-0 z-modal bg-white flex flex-col">
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">
                  Notifications {unreadCount > 0 && `(${unreadCount})`}
                </h3>
                <button onClick={() => setIsOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
              {unreadCount > 0 && (
                <div className="px-4 py-2 border-b border-gray-100">
                  <button onClick={handleMarkAllAsRead} disabled={markAllAsReadMutation.isPending} className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50">
                    Tout marquer comme lu
                  </button>
                </div>
              )}
              <div className="flex-1 overflow-y-auto">
                <NotificationList notifications={notifications} isLoading={isLoading} onItemClick={handleNotificationClick} variant="mobile" />
              </div>
            </div>,
            document.body
          )}

          {/* Desktop: Dropdown */}
          <div className="hidden md:flex absolute right-0 mt-2 w-96 bg-white rounded-2xl border border-gray-200 shadow-xl z-dropdown max-h-[600px] flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </h3>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllAsRead} disabled={markAllAsReadMutation.isPending} className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 transition-colors">
                  Tout marquer comme lu
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
              <NotificationList
                notifications={notifications}
                isLoading={isLoading}
                onItemClick={handleNotificationClick}
                onMarkAsRead={(id) => markAsReadMutation.mutate(id)}
                variant="desktop"
              />
            </div>
            {notifications.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200">
                <button onClick={() => { navigate('/editorial-calendar'); setIsOpen(false); }} className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
                  Voir tout le calendrier éditorial
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
